import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  ArrowLeft, 
  Building2, 
  User, 
  Phone, 
  Mail, 
  MapPin,
  Pencil,
  Trash2,
  FileText,
  Upload,
  Download,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Clock,
  Landmark,
  Send,
  Loader2,
  ExternalLink,
  FileDown,
  ShieldCheck,
  ShieldX,
  Star,
  ClipboardList
} from 'lucide-react';
import { toast } from 'sonner';
import DocumentUpload from '../components/DocumentUpload';
import DocumentVersions from '../components/DocumentVersions';
import SupplierRating, { AverageStars } from '../components/SupplierRating';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SupplierDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [supplier, setSupplier] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteDocDialog, setDeleteDocDialog] = useState({ open: false, doc: null });
  const [showUpload, setShowUpload] = useState(false);
  
  // Portal invitation state
  const [portalStatus, setPortalStatus] = useState(null);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);

  // Compliance state
  const [complianceData, setComplianceData] = useState(null);
  const [showComplianceDialog, setShowComplianceDialog] = useState(false);
  const [complianceNotes, setComplianceNotes] = useState('');
  const [savingCompliance, setSavingCompliance] = useState(false);

  const fetchSupplier = useCallback(async () => {
    try {
      const [supplierRes, docsRes, portalRes, complianceRes] = await Promise.all([
        axios.get(`${API}/suppliers/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/suppliers/${id}/documents`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/suppliers/${id}/portal-status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/suppliers/${id}/compliance`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setSupplier(supplierRes.data);
      setDocuments(docsRes.data);
      setPortalStatus(portalRes.data);
      setInviteEmail(supplierRes.data.email || '');
      setComplianceData(complianceRes.data);
      setComplianceNotes(complianceRes.data.compliance_notes || '');
    } catch (error) {
      console.error('Failed to fetch supplier:', error);
      toast.error('Failed to load supplier');
      navigate('/suppliers');
    } finally {
      setLoading(false);
    }
  }, [id, token, navigate]);

  useEffect(() => {
    fetchSupplier();
  }, [fetchSupplier]);

  const handleSendInvite = async () => {
    if (!inviteEmail) {
      toast.error('Please enter an email address');
      return;
    }

    setSendingInvite(true);
    try {
      const response = await axios.post(
        `${API}/supplier-portal/invite/${id}`,
        { email: inviteEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.email_sent) {
        toast.success('Portal invitation sent successfully');
      } else {
        toast.warning('Invitation created but email delivery may have failed. Check SendGrid configuration.');
      }
      
      setShowInviteDialog(false);
      fetchSupplier(); // Refresh portal status
    } catch (error) {
      console.error('Failed to send invite:', error);
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setSendingInvite(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/suppliers/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Supplier deleted successfully');
      navigate('/suppliers');
    } catch (error) {
      console.error('Failed to delete supplier:', error);
      toast.error('Failed to delete supplier');
    }
  };

  const handleDeleteDocument = async () => {
    if (!deleteDocDialog.doc) return;
    
    try {
      await axios.delete(`${API}/documents/${deleteDocDialog.doc.document_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document deleted successfully');
      fetchSupplier();
    } catch (error) {
      console.error('Failed to delete document:', error);
      toast.error('Failed to delete document');
    } finally {
      setDeleteDocDialog({ open: false, doc: null });
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(`${API}/documents/${doc.document_id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download document');
    }
  };

  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const downloadSupplierPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(`${API}/reports/supplier/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const safeName = supplier.company_name.replace(/[^\w\s-]/g, '').replace(/\s+/g, '_');
      link.setAttribute('download', `${safeName}_report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF report downloaded');
    } catch (error) {
      console.error('PDF download failed:', error);
      toast.error('Failed to download PDF report');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleUpdateCompliance = async (manuallyCompliant) => {
    setSavingCompliance(true);
    try {
      await axios.post(`${API}/suppliers/${id}/compliance`, {
        manually_compliant: manuallyCompliant,
        compliance_notes: complianceNotes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(manuallyCompliant ? 'Supplier marked as compliant' : 'Compliance status removed');
      setShowComplianceDialog(false);
      fetchSupplier(); // Refresh data
    } catch (error) {
      console.error('Failed to update compliance:', error);
      toast.error('Failed to update compliance status');
    } finally {
      setSavingCompliance(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      onboarded: 'status-active',
      under_review: 'status-pending',
      on_hold: 'status-warning',
      do_not_use: 'status-danger',
      inactive: 'status-inactive'
    };
    return styles[status] || 'status-inactive';
  };

  const getDocumentStatus = (doc) => {
    if (!doc.expiry_date) return { status: 'valid', label: 'No Expiry', icon: CheckCircle, color: 'text-green-500' };
    
    const expiry = new Date(doc.expiry_date);
    const now = new Date();
    const thirtyDays = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    
    if (expiry < now) {
      return { status: 'expired', label: 'Expired', icon: AlertTriangle, color: 'text-red-500' };
    } else if (expiry < thirtyDays) {
      return { status: 'expiring', label: 'Expiring Soon', icon: Clock, color: 'text-orange-500' };
    }
    return { status: 'valid', label: 'Valid', icon: CheckCircle, color: 'text-green-500' };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      contract: 'Contract',
      insurance: 'Insurance',
      compliance: 'Compliance',
      accreditation: 'Accreditation',
      health_safety: 'Health & Safety',
      risk_assessment: 'Risk Assessment',
      method_statement: 'Method Statement',
      other: 'Other'
    };
    return labels[type] || type;
  };

  const documentsByType = documents.reduce((acc, doc) => {
    const type = doc.document_type || 'other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(doc);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!supplier) return null;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="supplier-detail">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/suppliers')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl sm:text-3xl font-bold font-heading tracking-tight">
                {supplier.company_name}
              </h1>
              <Badge className={`${getStatusBadge(supplier.status)} capitalize`}>
                {supplier.status.replace('_', ' ')}
              </Badge>
              {supplier.average_rating > 0 && (
                <div className="flex items-center gap-1 text-sm">
                  <AverageStars rating={supplier.average_rating} size="small" />
                  <span className="text-muted-foreground">({supplier.rating_count || 0})</span>
                </div>
              )}
            </div>
            <p className="text-muted-foreground mt-1">
              {(supplier.sectors || [supplier.sector]).join(', ')} • {(supplier.areas_covered || [supplier.location]).filter(Boolean).join(', ')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Portal Status & Invite Button */}
          {portalStatus?.has_portal_access ? (
            <Badge className="status-active">
              <CheckCircle className="w-3 h-3 mr-1" />
              Portal Active
            </Badge>
          ) : portalStatus?.status === 'invitation_pending' ? (
            <Button variant="outline" onClick={() => setShowInviteDialog(true)}>
              <Clock className="w-4 h-4 mr-2" />
              Resend Invite
            </Button>
          ) : (
            <Button variant="outline" onClick={() => setShowInviteDialog(true)} data-testid="invite-portal-btn">
              <Send className="w-4 h-4 mr-2" />
              Invite to Portal
            </Button>
          )}
          <SupplierRating 
            supplierId={id}
            supplierName={supplier.company_name}
            onRatingAdded={fetchSupplier}
          />
          <Button 
            variant="outline"
            onClick={downloadSupplierPdf}
            disabled={downloadingPdf}
            data-testid="download-pdf-btn"
          >
            {downloadingPdf ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileDown className="w-4 h-4 mr-2" />
            )}
            PDF
          </Button>
          <Link to={`/suppliers/${id}/edit`}>
            <Button variant="outline" data-testid="edit-supplier-btn">
              <Pencil className="w-4 h-4 mr-2" />
              Edit
            </Button>
          </Link>
          <Button 
            variant="outline" 
            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
            onClick={() => setDeleteDialog(true)}
            data-testid="delete-supplier-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      {/* Content Tabs */}
      <Tabs defaultValue="details" className="space-y-6">
        <TabsList data-testid="supplier-tabs">
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="documents">Documents ({documents.length})</TabsTrigger>
          <TabsTrigger value="notes">Notes</TabsTrigger>
        </TabsList>

        {/* Details Tab */}
        <TabsContent value="details" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Contact Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <User className="w-5 h-5 text-primary" />
                  Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Contact Person</p>
                    <p className="font-medium">{supplier.contact_person}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <a href={`mailto:${supplier.email}`} className="font-medium text-primary hover:underline">
                      {supplier.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Phone</p>
                    <a href={`tel:${supplier.phone}`} className="font-medium text-primary hover:underline">
                      {supplier.phone}
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Company Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-primary" />
                  Company Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Sectors</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(supplier.sectors || [supplier.sector]).map((s, i) => (
                        <Badge key={i} variant="secondary">{s}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Area's Covered</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(supplier.areas_covered || [supplier.location]).filter(Boolean).map((a, i) => (
                        <Badge key={i} variant="outline">{a}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Address</p>
                    <p className="font-medium">{supplier.address}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Banking Details */}
            {(supplier.bank_account_name || supplier.bank_sort_code || supplier.bank_account_number) && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-heading flex items-center gap-2">
                    <Landmark className="w-5 h-5 text-primary" />
                    Banking Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {supplier.bank_account_name && (
                    <div className="flex items-start gap-3">
                      <Landmark className="w-5 h-5 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-sm text-muted-foreground">Account Name</p>
                        <p className="font-medium">{supplier.bank_account_name}</p>
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    {supplier.bank_sort_code && (
                      <div>
                        <p className="text-sm text-muted-foreground">Sort Code</p>
                        <p className="font-medium font-mono">{supplier.bank_sort_code}</p>
                      </div>
                    )}
                    {supplier.bank_account_number && (
                      <div>
                        <p className="text-sm text-muted-foreground">Account Number</p>
                        <p className="font-medium font-mono">{supplier.bank_account_number}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Company Registration Information */}
            {(supplier.vat_number || supplier.company_registration_number) && (
              <Card data-testid="company-registration-card">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 font-heading">
                    <ClipboardList className="w-5 h-5 text-primary" />
                    Company Registration Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {supplier.vat_number && (
                      <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                        <ClipboardList className="w-5 h-5 text-muted-foreground mt-0.5" />
                        <div>
                          <p className="text-xs text-muted-foreground">VAT Number</p>
                          <p className="font-medium">{supplier.vat_number}</p>
                        </div>
                      </div>
                    )}
                    {supplier.company_registration_number && (
                      <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                        <ClipboardList className="w-5 h-5 text-muted-foreground mt-0.5" />
                        <div>
                          <p className="text-xs text-muted-foreground">Company Registration Number</p>
                          <p className="font-medium">{supplier.company_registration_number}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Compliance Status */}
            <Card className="col-span-1 lg:col-span-2" data-testid="compliance-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 font-heading">
                  {complianceData?.manually_compliant ? (
                    <ShieldCheck className="w-5 h-5 text-green-500" />
                  ) : (
                    <ShieldX className="w-5 h-5 text-muted-foreground" />
                  )}
                  Compliance Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Compliance Status Badge */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Badge 
                        variant={complianceData?.status === 'compliant' ? 'default' : complianceData?.status === 'warning' ? 'warning' : 'destructive'}
                        className={
                          complianceData?.status === 'compliant' 
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                            : complianceData?.status === 'warning'
                            ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                            : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                        }
                      >
                        {complianceData?.status === 'compliant' ? 'Compliant' : complianceData?.status === 'warning' ? 'Warning' : 'Non-Compliant'}
                      </Badge>
                      {complianceData?.manually_compliant && (
                        <Badge variant="outline" className="text-xs">
                          Manually Verified
                        </Badge>
                      )}
                    </div>
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => setShowComplianceDialog(true)}
                      data-testid="manage-compliance-btn"
                    >
                      {complianceData?.manually_compliant ? 'Edit Compliance' : 'Mark as Compliant'}
                    </Button>
                  </div>

                  {/* Compliance Details */}
                  {complianceData && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2 border-t">
                      <div className="text-center p-3 bg-muted/50 rounded-lg">
                        <p className="text-2xl font-bold text-green-600">{complianceData.total_documents}</p>
                        <p className="text-xs text-muted-foreground">Documents</p>
                      </div>
                      <div className="text-center p-3 bg-muted/50 rounded-lg">
                        <p className="text-2xl font-bold text-yellow-600">{complianceData.expiring_documents?.length || 0}</p>
                        <p className="text-xs text-muted-foreground">Expiring Soon</p>
                      </div>
                      <div className="text-center p-3 bg-muted/50 rounded-lg">
                        <p className="text-2xl font-bold text-red-600">{complianceData.expired_documents?.length || 0}</p>
                        <p className="text-xs text-muted-foreground">Expired</p>
                      </div>
                    </div>
                  )}

                  {/* Missing Documents Warning */}
                  {complianceData?.missing_documents?.length > 0 && !complianceData?.manually_compliant && (
                    <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                      <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-1">Missing Documents</p>
                      <p className="text-xs text-yellow-600 dark:text-yellow-400">
                        {complianceData.missing_documents.map(d => d.replace('_', ' ')).join(', ')}
                      </p>
                    </div>
                  )}

                  {/* Compliance Notes */}
                  {complianceData?.compliance_notes && (
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">Compliance Notes</p>
                      <p className="text-sm">{complianceData.compliance_notes}</p>
                    </div>
                  )}

                  {/* Verified Date */}
                  {complianceData?.compliance_verified_at && (
                    <p className="text-xs text-muted-foreground">
                      Last verified: {new Date(complianceData.compliance_verified_at).toLocaleDateString('en-GB', { 
                        day: 'numeric', 
                        month: 'short', 
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-heading">Supplier Documents</h3>
            <Button onClick={() => setShowUpload(true)} data-testid="upload-document-btn">
              <Upload className="w-4 h-4 mr-2" />
              Upload Document
            </Button>
          </div>

          {documents.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium mb-2">No documents uploaded</h3>
                <p className="text-muted-foreground mb-4">
                  Upload contracts, insurance certificates, and compliance documents
                </p>
                <Button onClick={() => setShowUpload(true)}>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload First Document
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {['contract', 'insurance', 'compliance', 'accreditation', 'health_safety', 'risk_assessment', 'method_statement', 'other'].map((type) => (
                <Card key={type}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base font-heading flex items-center gap-2">
                      <FileText className="w-4 h-4 text-primary" />
                      {getDocumentTypeLabel(type)}
                      <Badge variant="secondary" className="ml-auto">
                        {documentsByType[type]?.length || 0}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {documentsByType[type]?.length > 0 ? (
                      <div className="space-y-2">
                        {documentsByType[type].map((doc) => {
                          const docStatus = getDocumentStatus(doc);
                          const StatusIcon = docStatus.icon;
                          return (
                            <div 
                              key={doc.document_id}
                              className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                              data-testid={`document-${doc.document_id}`}
                            >
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                <StatusIcon className={`w-4 h-4 shrink-0 ${docStatus.color}`} />
                                <div className="min-w-0">
                                  <p className="font-medium text-sm truncate">{doc.original_filename}</p>
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <span>{formatFileSize(doc.size)}</span>
                                    {doc.expiry_date && (
                                      <>
                                        <span>•</span>
                                        <span className={docStatus.status === 'expired' ? 'text-red-500' : ''}>
                                          Expires: {formatDate(doc.expiry_date)}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <DocumentVersions 
                                  documentId={doc.document_id}
                                  documentName={doc.original_filename}
                                  onVersionUploaded={fetchSupplier}
                                />
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  onClick={() => handleDownload(doc)}
                                  data-testid={`download-${doc.document_id}`}
                                >
                                  <Download className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  onClick={() => setDeleteDocDialog({ open: true, doc })}
                                  className="text-red-500 hover:text-red-600"
                                  data-testid={`delete-doc-${doc.document_id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No {getDocumentTypeLabel(type).toLowerCase()} documents
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Notes & Comments</CardTitle>
            </CardHeader>
            <CardContent>
              {supplier.notes ? (
                <p className="whitespace-pre-wrap">{supplier.notes}</p>
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  No notes added yet. Edit the supplier to add notes.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Document Upload Dialog */}
      <DocumentUpload 
        open={showUpload} 
        onClose={() => setShowUpload(false)} 
        supplierId={id}
        onSuccess={fetchSupplier}
      />

      {/* Delete Supplier Dialog */}
      <AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Supplier</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{supplier.company_name}"? 
              This will also remove all associated documents. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Document Dialog */}
      <AlertDialog open={deleteDocDialog.open} onOpenChange={(open) => setDeleteDocDialog({ open, doc: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteDocDialog.doc?.original_filename}"? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteDocument} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Invite to Portal Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Supplier to Portal</DialogTitle>
            <DialogDescription>
              Send an invitation to allow this supplier to access their self-service portal
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Email Address</Label>
              <Input
                type="email"
                placeholder="supplier@company.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                data-testid="invite-email-input"
              />
              <p className="text-xs text-muted-foreground">
                An invitation link will be sent to this email address
              </p>
            </div>
            
            {portalStatus?.status === 'invitation_pending' && (
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <p className="text-sm text-yellow-600 dark:text-yellow-400">
                  An invitation was previously sent on {new Date(portalStatus.invitation_sent_at).toLocaleDateString()}.
                  Sending a new invitation will invalidate the previous one.
                </p>
              </div>
            )}
            
            {portalStatus?.status === 'invitation_expired' && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">
                  The previous invitation has expired. Send a new one.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendInvite} disabled={sendingInvite || !inviteEmail} data-testid="send-invite-btn">
              {sendingInvite ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Invitation
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Compliance Dialog */}
      <Dialog open={showComplianceDialog} onOpenChange={setShowComplianceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-green-500" />
              Manual Compliance Verification
            </DialogTitle>
            <DialogDescription>
              Mark this supplier as compliant after manually verifying their documentation.
              This overrides the automatic compliance checks.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Current Status */}
            <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
              <Checkbox 
                id="manually-compliant"
                checked={complianceData?.manually_compliant || false}
                onCheckedChange={(checked) => {
                  setComplianceData(prev => ({ ...prev, manually_compliant: checked }));
                }}
                data-testid="compliance-checkbox"
              />
              <Label htmlFor="manually-compliant" className="flex-1 cursor-pointer">
                <span className="font-medium">Mark as Compliant</span>
                <p className="text-xs text-muted-foreground mt-0.5">
                  I have manually verified all required documentation for this supplier
                </p>
              </Label>
            </div>

            {/* Compliance Notes */}
            <div className="space-y-2">
              <Label htmlFor="compliance-notes">Verification Notes (optional)</Label>
              <Textarea
                id="compliance-notes"
                placeholder="Add notes about the compliance verification..."
                value={complianceNotes}
                onChange={(e) => setComplianceNotes(e.target.value)}
                rows={3}
                data-testid="compliance-notes-input"
              />
            </div>

            {/* Warning about missing docs */}
            {complianceData?.missing_documents?.length > 0 && (
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                      System detected missing documents
                    </p>
                    <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-0.5">
                      {complianceData.missing_documents.map(d => d.replace('_', ' ')).join(', ')}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowComplianceDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => handleUpdateCompliance(complianceData?.manually_compliant || false)}
              disabled={savingCompliance}
              className={complianceData?.manually_compliant ? 'bg-green-600 hover:bg-green-700' : ''}
              data-testid="save-compliance-btn"
            >
              {savingCompliance ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <ShieldCheck className="w-4 h-4 mr-2" />
                  {complianceData?.manually_compliant ? 'Save Compliance' : 'Clear Compliance'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupplierDetail;
